import React from 'react';
import { BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { BarChart3, Map, Table2 } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const COLORS = ['#E3000F', '#FFC107', '#009246', '#3B82F6', '#A855F7', '#F97316', '#06B6D4', '#EC4899'];

const ChartViz = ({ visualization }) => {
  const { chart_type, title, data } = visualization;

  return (
    <div className="mt-3" data-testid="chat-chart">
      <div className="flex items-center gap-2 mb-3">
        <BarChart3 className="w-4 h-4 text-[#E3000F]" />
        <span className="text-xs uppercase tracking-wider text-[#A1A1AA] font-mono">{title}</span>
      </div>
      <div className="bg-[#0A0A0A] border border-[#27272A] p-4" style={{ height: 280 }}>
        <ResponsiveContainer width="100%" height="100%">
          {chart_type === 'pie' ? (
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name, value }) => `${name}: ${value}`}>
                {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: '#1A1A1A', border: '1px solid #27272A', color: '#fff' }} />
              <Legend wrapperStyle={{ color: '#A1A1AA', fontSize: 12 }} />
            </PieChart>
          ) : chart_type === 'line' ? (
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
              <XAxis dataKey="name" stroke="#71717A" tick={{ fontSize: 11 }} />
              <YAxis stroke="#71717A" tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={{ background: '#1A1A1A', border: '1px solid #27272A', color: '#fff' }} />
              <Line type="monotone" dataKey="value" stroke="#E3000F" strokeWidth={2} dot={{ fill: '#E3000F' }} />
            </LineChart>
          ) : (
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
              <XAxis dataKey="name" stroke="#71717A" tick={{ fontSize: 11 }} />
              <YAxis stroke="#71717A" tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={{ background: '#1A1A1A', border: '1px solid #27272A', color: '#fff' }} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const MapViz = ({ visualization }) => {
  const { title, center, zoom, markers } = visualization;

  return (
    <div className="mt-3" data-testid="chat-map">
      <div className="flex items-center gap-2 mb-3">
        <Map className="w-4 h-4 text-[#E3000F]" />
        <span className="text-xs uppercase tracking-wider text-[#A1A1AA] font-mono">{title}</span>
      </div>
      <div className="bg-[#0A0A0A] border border-[#27272A] overflow-hidden" style={{ height: 320 }}>
        <MapContainer center={center || [44.22, 12.04]} zoom={zoom || 13} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org">OSM</a>'
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
          />
          {markers?.map((m, i) => (
            <Marker key={i} position={[m.lat, m.lng]}>
              <Popup>
                <div className="text-sm">
                  <strong>{m.label}</strong>
                  {m.address && <p className="text-xs mt-1">{m.address}</p>}
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
    </div>
  );
};

const TableViz = ({ visualization }) => {
  const { title, columns, rows } = visualization;

  return (
    <div className="mt-3" data-testid="chat-table">
      <div className="flex items-center gap-2 mb-3">
        <Table2 className="w-4 h-4 text-[#E3000F]" />
        <span className="text-xs uppercase tracking-wider text-[#A1A1AA] font-mono">{title}</span>
      </div>
      <div className="bg-[#0A0A0A] border border-[#27272A] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#27272A]">
              {columns?.map((col, i) => (
                <th key={i} className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-[#A1A1AA] font-mono">{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows?.map((row, ri) => (
              <tr key={ri} className="border-b border-[#27272A]/50 hover:bg-[#1A1A1A] transition-colors">
                {row.map((cell, ci) => (
                  <td key={ci} className="px-3 py-2 text-[#D4D4D8] text-xs">{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const ChatVisualization = ({ visualization }) => {
  if (!visualization) return null;

  switch (visualization.type) {
    case 'chart': return <ChartViz visualization={visualization} />;
    case 'map': return <MapViz visualization={visualization} />;
    case 'table': return <TableViz visualization={visualization} />;
    default: return null;
  }
};
